import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ServerOff, RefreshCcw } from "lucide-react";

const BACKEND_CHECK_URL =
  process.env.REACT_APP_BACKEND_CHECK_URL || "/api/auth/login";

const BACKEND_DOWN_PATH = "/backend-down";

export const BackendDownPage = () => {
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState("");

  const retry = async () => {
    if (checking) {
      return;
    }

    setChecking(true);
    setMessage("");

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`${BACKEND_CHECK_URL}?t=${Date.now()}`, {
        method: "OPTIONS",
        cache: "no-store",
        signal: controller.signal,
      });

      // If Nginx cannot reach Spring Boot, it commonly returns 502/503/504.
      if ([502, 503, 504].includes(response.status)) {
        throw new Error(`Backend unavailable. HTTP ${response.status}`);
      }

      let returnTo =
        sessionStorage.getItem("backendDownReturnTo") || "/";

      if (
        !returnTo ||
        returnTo === BACKEND_DOWN_PATH ||
        returnTo.startsWith(`${BACKEND_DOWN_PATH}?`)
      ) {
        returnTo = "/";
      }

      sessionStorage.removeItem("backendDownReturnTo");

      window.location.assign(returnTo);
    } catch (error) {
      setMessage(
        error.name === "AbortError"
          ? "The server is taking too long to respond. Please try again."
          : "Backend is still unavailable. Please check the server and try again."
      );
    } finally {
      clearTimeout(timeoutId);
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-slate-50">
      <div className="max-w-lg w-full bms-card p-8 text-center">
        <div className="h-16 w-16 rounded-2xl bg-rose-50 text-rose-600 grid place-items-center mx-auto">
          <ServerOff className="h-8 w-8" />
        </div>

        <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">
          Backend service is unavailable
        </h1>

        <p className="text-slate-500 mt-3">
          The application cannot connect to the server right now. Please make
          sure the backend is running and try again.
        </p>

        <p className="text-sm text-slate-600 mt-3">
          If the issue continues, please contact admin on{" "}
          <a
            href="tel:0768334507"
            className="font-semibold text-navy-800 hover:underline"
          >
            0768334507
          </a>
          .
        </p>

        {message && (
          <div className="mt-4 rounded-lg bg-rose-50 text-rose-700 text-sm px-4 py-3">
            {message}
          </div>
        )}

        <Button
          type="button"
          onClick={retry}
          disabled={checking}
          className="mt-6 bg-navy-800 hover:bg-navy-900 text-white"
        >
          <RefreshCcw
            className={`h-4 w-4 mr-2 ${
              checking ? "animate-spin" : ""
            }`}
          />

          {checking ? "Checking..." : "Retry Connection"}
        </Button>
      </div>
    </div>
  );
};